import { ThrottledError, throttledUntil } from '../host-throttle';

/** How often a task is put back after hitting a throttled domain before it is given up for the
 *  run. With the backoff doubling from 30s, that's waiting about 15 minutes. */
const MAX_REQUEUES = 5;

interface Task {
  key: string;
  run: () => Promise<void>;
  giveUp: () => Promise<void> | void;
  requeues: number;
  /** Not before then (ms epoch): the block of the domain that refused it. */
  notBefore: number;
}

/**
 * Runs download tasks with limited concurrency, overall and per throttle key (domain, see
 * host-throttle.ts). Tasks of a throttled key wait while other keys' tasks run, and free slots go
 * round-robin over the keys, so one big domain can't starve the rest. A task that throws
 * ThrottledError goes back into the queue until the block ends; after MAX_REQUEUES its giveUp runs
 * instead. Other errors are the task's own business: it should handle them. Tasks can be added
 * while the queue runs. Aborting the signal drops everything still queued.
 */
export class HostQueue {
  private queues = new Map<string, Task[]>();
  private active = 0;
  private activePerKey = new Map<string, number>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private idleWaiters: (() => void)[] = [];

  constructor(private signal: AbortSignal, private concurrency: number, private perKey: number) {
    signal.addEventListener('abort', () => this.pump());
  }

  add(key: string, run: () => Promise<void>, giveUp: () => Promise<void> | void = () => {}): void {
    this.enqueue({ key, run, giveUp, requeues: 0, notBefore: 0 });
    this.pump();
  }

  /** Resolves once no task is queued or running. */
  whenIdle(): Promise<void> {
    return this.isIdle() ? Promise.resolve() : new Promise(resolve => this.idleWaiters.push(resolve));
  }

  private isIdle(): boolean {
    return !this.active && !this.queues.size;
  }

  private enqueue(task: Task) {
    const queue = this.queues.get(task.key);
    if (queue) queue.push(task);
    else this.queues.set(task.key, [task]);
  }

  private pump(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    if (this.signal.aborted) this.queues.clear();
    const now = Date.now();
    let wakeAt = Infinity;
    for (const [key, queue] of Array.from(this.queues)) {
      if (this.active >= this.concurrency) break;
      const until = Math.max(throttledUntil(key, now), queue[0].notBefore);
      if (until > now) {
        wakeAt = Math.min(wakeAt, until);
        continue;
      }
      while (queue.length && queue[0].notBefore <= now && this.active < this.concurrency && (this.activePerKey.get(key) ?? 0) < this.perKey)
        this.start(queue.shift()!);
      // Round-robin: the key moves to the back, so the next free slot goes to another one.
      this.queues.delete(key);
      if (queue.length) this.queues.set(key, queue);
    }
    if (wakeAt < Infinity) this.timer = setTimeout(() => this.pump(), wakeAt - now);
    if (this.isIdle()) this.idleWaiters.splice(0).forEach(resolve => resolve());
  }

  private async start(task: Task): Promise<void> {
    this.active++;
    this.activePerKey.set(task.key, (this.activePerKey.get(task.key) ?? 0) + 1);
    try {
      await task.run();
    } catch (e) {
      if (this.signal.aborted) {
        // dropped with the rest of the queue
      } else if (!(e instanceof ThrottledError))
        console.error('Download task failed', e);
      else if (++task.requeues <= MAX_REQUEUES) {
        task.notBefore = e.until;
        this.enqueue(task);
      } else
        await Promise.resolve().then(task.giveUp).catch(e2 => console.error('Download task failed', e2));
    } finally {
      this.active--;
      const left = this.activePerKey.get(task.key)! - 1;
      if (left) this.activePerKey.set(task.key, left);
      else this.activePerKey.delete(task.key);
      this.pump();
    }
  }
}
